import React, {useState} from "react";
import Project from "../models/Project";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useParams} from "react-router-dom";
import {useMount, useUnmount} from "react-use";
import ProjectPosition from "../models/ProjectPosition";
import User from "../models/User";
import {useAuth} from "./AuthProvider";
import {useAPI} from "../config/API";

let ProjectFormContext = React.createContext(null);

const ProjectFormProvider = ({children}) => {

    const allParams = useParams();
    const {projectId} = useParams();
    const queryClient = useQueryClient();
    const {auth, update: updateAuth} = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const [formModel, setFormModel] = useState(null);
    const [project, setProject] = useState(null);
    const {
        data: projectData,
        isFetching,
        isInitialLoading: isProjectDataLoading,
        isRefetching,
        error,
        refetch
    } = useAPI(['fetchProject', parseInt(projectId)],  {
            enabled: (!!projectId || !queryClient.getQueryData(['fetchProject', parseInt(projectId)])) && projectId !== 'new',
            onSuccess: async(response) => {
                await setProject(response);
                await setFormModel(Project.make({...response}))
                setIsLoading(false);
            },
            // onError: (e) => {
            //     console.warn('ERROR: onError', e);
            //     setIsLoading(false)
            // }
        }
    )


    useMount(async() => {
        if(projectId && projectId !== 'new') {

            let prefetchedProject = queryClient.getQueryData(['fetchProject', parseInt(projectId)]);
            if (prefetchedProject) {
                await setProject(prefetchedProject);
                await setFormModel(Project.make({...prefetchedProject}));

            }else {
                return;
            }
        }else {
            let assignedUser = TB.pick(auth || {}, ['id', 'first_name', 'last_name', 'client_project_groups', 'email']);

            assignedUser = User.make({
                ...assignedUser,
                is_owner: true
            })
            let newProject = {
                id: 'new',
                assigned_users: [assignedUser]
            }
            // TODO Change with updated platform features for teams
            // if (!laravel.platform_features.clients.services) {
            //     newProject.type = TB.invert(laravel.project_types)[Project.ProjectType.BYO]
            // }
            await setProject(Project.make(newProject))

            await setFormModel(Project.make({...newProject}))
        }


        await setIsLoading(false)
    })

    useUnmount(() => {
        console.log('unmount')
        return queryClient.cancelQueries(['fetchProject', parseInt(projectId)],{fetching: true})
    })
    const updateProjectMutation = useMutation(['updateProject'],
        async(params) => {
            if(params?.id !== 'new') {
                return formModel?.update(params);
            }else {
                let newProject = {
                    name: params.name,
                    type: params.type,
                    posts_on_talent_job_board: params.posts_on_talent_job_board,
                    assigned_users: params.assigned_users,
                }
                newProject.service_id = params.service ? parseInt(
                    params.service.id
                ) : null;

                newProject.type = parseInt(params.type);

                if (TB.isDefined(params.client_project_group_id)) {
                    newProject.client_project_group_id = params.client_project_group_id;
                    if (params.client_project_group_id !== auth?.last_client_project_group_id) {
                        await updateAuth(User.make({...(auth || {}), last_client_project_group_id: project.client_project_group_id}))
                    }
                }

                await setFormModel(Project.make(newProject))

                return Project.create(newProject);
            }
        }, {
            onError: async(e) => {
                await setIsDisabled(false)
                return  TB.showServerErrorToast()
            },
            onSuccess: async(updatedProject) => {
                await setIsDisabled(false)
                if (!updatedProject) {
                    TB.showServerErrorToast()
                    return false;
                }
                // await setProject(updatedProject)
                // await setFormModel(updatedProject)
                // await updateCallback(updatedProject);
            }
        }
    )
    const createChangeOrderMutation = useMutation(['createChangeOrder'],
        async(params) => {
            return project.createChangeOrder(params)
        }, {
            onError: async(e) => {
                await setIsDisabled(false)
                return  TB.showServerErrorToast()
            },
            onSuccess: async(changeOrder) => {
                await setIsDisabled(false)
                if (!changeOrder) {
                    TB.showServerErrorToast()
                    return false;
                }

                return changeOrder;
            }
        }
    )

    const publishChangeOrderMutation = useMutation(['publishChangeOrder'],
        async({changeOrderId, params}) => {
            return project.publishChangeOrder(changeOrderId, params)
        }, {
            onError: async(e) => {
                await setIsDisabled(false)
                return  TB.showServerErrorToast()
            },
            onSuccess: async(changeOrder) => {
                await setIsDisabled(false)
                if (!changeOrder) {
                    TB.showServerErrorToast()
                    return false;
                }

                return changeOrder;
            }
        }
    )
    const deleteChangeOrderMutation = useMutation(['deleteChangeOrder'],
        async({changeOrderId, params}) => {
            return project.deleteChangeOrder(changeOrderId, params)
        }, {
            onError: async(e) => {
                await setIsDisabled(false)
                return  TB.showServerErrorToast()
            },
            onSuccess: async(project) => {
                await setIsDisabled(false)
                if (!project) {
                    TB.showServerErrorToast()
                    return false;
                }

                return project;
            }
        }
    )
    const updateChangeOrderMutation = useMutation(['updateChangeOrder'],
        async({changeOrderId, params}) => {
            return project.updateChangeOrder(changeOrderId, params)
        }, {
            onError: async(e) => {
                await setIsDisabled(false)
                return  TB.showServerErrorToast()
            },
            onSuccess: async(changeOrder) => {
                await setIsDisabled(false)
                if (!changeOrder) {
                    TB.showServerErrorToast()
                    return false;
                }

                return changeOrder;
            }
        }
    )

    const [uniqId, setUniqId] = useState(TB.uuid());

    const updateQueryCallback = async(updatedProject) => {
        console.log('ProjectFormProvider@updateCallback',updatedProject.id)

        if (parseInt(updatedProject?.id) === parseInt(projectId)) {
            await setProject(updatedProject);
            await setFormModel(Project.make({...updatedProject}))
            await queryClient.setQueryData(['fetchProject', parseInt(projectId)], previousData => {
                if (!previousData) return undefined;

                return Project.make({...updatedProject})
            })
        }
    }

    useMount(() => {
        TB.event().listen("Projects@updated", updateQueryCallback, uniqId)
    })

    useUnmount(() => {
        TB.event().provider().off("Projects@updated", updateQueryCallback, uniqId)
    })

    const onFormSubmit = async(projectProps) => {
        await setIsDisabled(true)
        let updatedProject = await updateProjectMutation.mutateAsync(projectProps);

        if(!updatedProject){
            let PostRequest = projectProps?.id === 'new' ? Project.CreateRequest : Project.SyncRequest
            PostRequest.didHandleError = await PostRequest.handleError();
            if (PostRequest.didHandleError
                && PostRequest.shouldRerun) {
                return onFormSubmit(projectProps)
            }
        }

        // await setProject(Project.make({...updatedProject}))
        // await setFormModel(Project.make({...updatedProject}))

        // await updateQueryCallback(updatedProject)
        return updatedProject;
    }

    const value = {
        project,
        formModel,
        error,
        refetch,
        isLoading: isLoading || isProjectDataLoading,
        isDisabled: isDisabled || updateProjectMutation.isLoading ||
            createChangeOrderMutation.isLoading ||
            publishChangeOrderMutation.isLoading ||
            updateChangeOrderMutation.isLoading,
        onFormChange: (params) => {
            let updatedModel = {...params};
            for (let [key, value] of Object.entries(params)) {
                if (TB.isDefined(value) && value instanceof Date) {
                    updatedModel[key] = value.toISOString();
                }
            }

            return setFormModel(Project.make({...formModel, ...params}))
        },
        isProjectUpdating: isDisabled || updateProjectMutation.isLoading ||
            createChangeOrderMutation.isLoading ||
            deleteChangeOrderMutation.isLoading ||
            publishChangeOrderMutation.isLoading ||
            updateChangeOrderMutation.isLoading,
        onFormReset: () => setFormModel(Project.make({...project})),
        onFormSubmit,
        createChangeOrder: async(props) => {
            if (!auth.canManageClients()) {
                return;
            }
            return createChangeOrderMutation.mutateAsync(props)
        },
        updateChangeOrder: async(changeOrderId, params) => {
            if (!auth.canManageClients()) {
                return;
            }
            return updateChangeOrderMutation.mutateAsync({changeOrderId, params})
        },

        deleteChangeOrder: async(changeOrderId) => {
            if (!auth.canManageClients()) {
                return;
            }
            return deleteChangeOrderMutation.mutateAsync({changeOrderId})
        },
        publishChangeOrder: async(changeOrderId) => {
            if (!auth.canManageClients()) {
                return;
            }
            return publishChangeOrderMutation.mutateAsync({changeOrderId})
        },
        onPositionChange: async (positionIndex, positionProps) => {

            if (positionIndex === -1) {
                return TB.showServerErrorToast();
            }

            let update = TB.set({...(formModel || {})}, `positions[${positionIndex}]`,
                ProjectPosition.make({...formModel?.positions[positionIndex], ...positionProps})
            );

            await setFormModel(Project.make(update));
            return update.positions;
        }
        // error,
        // refetch,
        // postUpdate,

        // projectError: error,

    }



    return (
        <ProjectFormContext.Provider value={value}>
            {children}
        </ProjectFormContext.Provider>
    );
}

const useProjectForm = () => {
    return React.useContext(ProjectFormContext);
}
export default ProjectFormProvider;
export {
    useProjectForm,
    ProjectFormContext
}